import React, { useEffect, useState } from 'react';
import TabBar from '../components/ui/TabBar';
import { listenCourses, upsertCourse, upsertAssignments, fetchSchoolCalendar } from './api';
import { buildManualAssignment, generateSeriesAssignments } from './authoring';
import { todayIso, weekNumberForDate } from './calendarMath';
import { BLOCK_IDS } from './schema';

const MODES = [
  { id: 'quick', label: 'Quick add' },
  { id: 'series', label: 'Series' },
];

// Parent-facing "define work" screen — the general-purpose authoring path
// that isn't tied to the fixed seed curriculum or a spreadsheet (gap-review
// item 2). Quick add is one assignment on one date; Series spreads one
// course's work across N weeks at H hours/week.
export default function AuthorTab({ familyData, student }) {
  const [mode, setMode] = useState('quick');
  const [courses, setCourses] = useState([]);

  useEffect(() => {
    if (!familyData?.id) return;
    return listenCourses(familyData.id, setCourses);
  }, [familyData?.id]);

  return (
    <div>
      <TabBar tabs={MODES} value={mode} onChange={setMode} style={{ marginTop: 12 }} />
      {mode === 'quick'
        ? <QuickAddForm familyData={familyData} student={student} courses={courses} />
        : <SeriesForm familyData={familyData} student={student} courses={courses} />}
    </div>
  );
}

// Fields shared by both forms: course picker with an inline "new course" escape
// hatch, since courses are otherwise only created via seed or CSV auto-discovery.
function CoursePicker({ courses, courseId, setCourseId, newCourseName, setNewCourseName }) {
  return (
    <>
      <div className="form-label" style={{ marginTop: 0 }}>Course</div>
      <select className="input" value={courseId} onChange={e => setCourseId(e.target.value)}>
        <option value="">— choose —</option>
        {courses.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        <option value="__new__">+ New course…</option>
      </select>
      {courseId === '__new__' && (
        <input className="input" style={{ marginTop: 6 }} placeholder="New course name"
          value={newCourseName} onChange={e => setNewCourseName(e.target.value)} />
      )}
    </>
  );
}

async function resolveCourseId(familyId, courseId, newCourseName, courses) {
  if (courseId !== '__new__') return courseId;
  const name = newCourseName.trim();
  if (!name) return null;
  const id = name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') || `course-${Date.now()}`;
  if (!courses.some(c => c.id === id)) {
    await upsertCourse(familyId, { id, name, subjectArea: 'Custom', colorToken: '--c-afternoon' });
  }
  return id;
}

function QuickAddForm({ familyData, student, courses }) {
  const [courseId, setCourseId] = useState('');
  const [newCourseName, setNewCourseName] = useState('');
  const [date, setDate] = useState(todayIso());
  const [block, setBlock] = useState('afternoon');
  const [title, setTitle] = useState('');
  const [subLabel, setSubLabel] = useState('');
  const [resourceLink, setResourceLink] = useState('');
  const [estMinutes, setEstMinutes] = useState(20);
  const [isFloor, setIsFloor] = useState(false);
  const [needsReview, setNeedsReview] = useState(true);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);

  const canSave = title.trim() && (courseId && (courseId !== '__new__' || newCourseName.trim())) && student?.studentEmail;

  async function save() {
    if (!canSave || !familyData?.id) return;
    setSaving(true);
    setResult(null);
    try {
      const resolvedCourseId = await resolveCourseId(familyData.id, courseId, newCourseName, courses);
      const calendar = await fetchSchoolCalendar(familyData.id);
      const assignment = buildManualAssignment({
        studentEmail: student.studentEmail,
        courseId: resolvedCourseId,
        date, block, title: title.trim(),
        subLabel: subLabel.trim(), resourceLink: resourceLink.trim(),
        estMinutes: Number(estMinutes) || 20,
        isFloor, needsReview, calendar,
      });
      await upsertAssignments(familyData.id, [assignment]);
      setResult(`Added "${title}" for ${date}.`);
      setTitle(''); setSubLabel(''); setResourceLink('');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{ marginTop: 12 }}>
      <CoursePicker courses={courses} courseId={courseId} setCourseId={setCourseId}
        newCourseName={newCourseName} setNewCourseName={setNewCourseName} />

      <div className="form-row" style={{ marginTop: 8 }}>
        <div>
          <div className="form-label" style={{ marginTop: 0 }}>Date</div>
          <input className="input" type="date" value={date} onChange={e => setDate(e.target.value)} />
        </div>
        <div>
          <div className="form-label" style={{ marginTop: 0 }}>Block</div>
          <select className="input" value={block} onChange={e => setBlock(e.target.value)}>
            {BLOCK_IDS.map(b => <option key={b} value={b}>{b}</option>)}
          </select>
        </div>
      </div>

      <div className="form-label">Title</div>
      <input className="input" placeholder='e.g. "Read — student choice"' value={title} onChange={e => setTitle(e.target.value)} />

      <div className="form-label">Details <span className="cp-edit-form__hint">(optional — leave blank for a lean assignment)</span></div>
      <textarea className="input" rows={2} placeholder="Book/video reference, worksheet, specific expectations…"
        value={subLabel} onChange={e => setSubLabel(e.target.value)} />

      <div className="form-label">Resource link <span className="cp-edit-form__hint">(optional)</span></div>
      <input className="input" placeholder="https://…" value={resourceLink} onChange={e => setResourceLink(e.target.value)} />

      <div className="form-row" style={{ marginTop: 8 }}>
        <div>
          <div className="form-label" style={{ marginTop: 0 }}>Est. minutes</div>
          <input className="input input--short" type="number" min={1} value={estMinutes}
            onChange={e => setEstMinutes(e.target.value)} />
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 22 }}>
          <input type="checkbox" checked={isFloor} onChange={e => setIsFloor(e.target.checked)} />
          <span style={{ fontSize: 13 }}>Counts toward the day's must-do floor</span>
        </label>
      </div>
      <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8 }}>
        <input type="checkbox" checked={needsReview} onChange={e => setNeedsReview(e.target.checked)} />
        <span style={{ fontSize: 13 }}>Needs a parent look when complete <span className="cp-edit-form__hint">(uncheck for e.g. free reading)</span></span>
      </label>

      <button className="btn btn--primary" style={{ marginTop: 12 }} disabled={!canSave || saving} onClick={save}>
        {saving ? 'Adding…' : 'Add assignment'}
      </button>
      {result && <p className="settings-note" style={{ marginTop: 8, color: 'var(--good)' }}>{result}</p>}
    </div>
  );
}

function SeriesForm({ familyData, student, courses }) {
  const [courseId, setCourseId] = useState('');
  const [newCourseName, setNewCourseName] = useState('');
  const [block, setBlock] = useState('afternoon');
  const [title, setTitle] = useState('');
  const [subLabel, setSubLabel] = useState('');
  const [resourceLink, setResourceLink] = useState('');
  const [weeksCount, setWeeksCount] = useState(6);
  const [sessionsPerWeek, setSessionsPerWeek] = useState(3);
  const [hoursPerWeek, setHoursPerWeek] = useState(3);
  const [startWeekNumber, setStartWeekNumber] = useState(null); // null = "current week", resolved on save
  const [isFloor, setIsFloor] = useState(true);
  const [needsReview, setNeedsReview] = useState(true);
  const [placementMode, setPlacementMode] = useState('student-placed');
  const [needsParent, setNeedsParent] = useState(false);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);

  const canSave = title.trim() && (courseId && (courseId !== '__new__' || newCourseName.trim())) && student?.studentEmail;
  const perSessionMinutes = sessionsPerWeek > 0 ? Math.round((hoursPerWeek * 60) / sessionsPerWeek) : 0;

  async function save() {
    if (!canSave || !familyData?.id) return;
    setSaving(true);
    setResult(null);
    try {
      const resolvedCourseId = await resolveCourseId(familyData.id, courseId, newCourseName, courses);
      const calendar = await fetchSchoolCalendar(familyData.id);
      if (!calendar?.weeks?.length) {
        setResult('No school calendar seeded yet — seed the year plan on the Setup tab first.');
        return;
      }
      const startWeek = startWeekNumber || weekNumberForDate(calendar.weeks, todayIso()) || 1;
      const assignments = generateSeriesAssignments({
        studentEmail: student.studentEmail,
        courseId: resolvedCourseId,
        title: title.trim(),
        subLabel: subLabel.trim(),
        resourceLink: resourceLink.trim(),
        block,
        weeksCount: Number(weeksCount) || 1,
        startWeekNumber: startWeek,
        sessionsPerWeek: Number(sessionsPerWeek) || 1,
        hoursPerWeek: Number(hoursPerWeek) || 1,
        isFloor, needsReview, placementMode, needsParent,
        calendar,
      });
      await upsertAssignments(familyData.id, assignments);
      setResult(`Created ${assignments.length} sessions across ${weeksCount} weeks (starting week ${startWeek}), ~${perSessionMinutes} min each.`);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{ marginTop: 12 }}>
      <CoursePicker courses={courses} courseId={courseId} setCourseId={setCourseId}
        newCourseName={newCourseName} setNewCourseName={setNewCourseName} />

      <div className="form-label">Title <span className="cp-edit-form__hint">(same for every session)</span></div>
      <input className="input" placeholder='e.g. "Dimensions Math — Unit 3"' value={title} onChange={e => setTitle(e.target.value)} />

      <div className="form-label">Details <span className="cp-edit-form__hint">(optional)</span></div>
      <textarea className="input" rows={2} value={subLabel} onChange={e => setSubLabel(e.target.value)} />

      <div className="form-label">Resource link <span className="cp-edit-form__hint">(optional)</span></div>
      <input className="input" value={resourceLink} onChange={e => setResourceLink(e.target.value)} />

      <div className="form-label">Block</div>
      <select className="input" value={block} onChange={e => setBlock(e.target.value)}>
        {BLOCK_IDS.map(b => <option key={b} value={b}>{b}</option>)}
      </select>

      <div className="form-row" style={{ marginTop: 8 }}>
        <div>
          <div className="form-label" style={{ marginTop: 0 }}>Weeks</div>
          <input className="input input--short" type="number" min={1} value={weeksCount} onChange={e => setWeeksCount(e.target.value)} />
        </div>
        <div>
          <div className="form-label" style={{ marginTop: 0 }}>Sessions / week</div>
          <input className="input input--short" type="number" min={1} max={5} value={sessionsPerWeek}
            onChange={e => setSessionsPerWeek(e.target.value)} />
        </div>
        <div>
          <div className="form-label" style={{ marginTop: 0 }}>Hours / week</div>
          <input className="input input--short" type="number" min={0.25} step={0.25} value={hoursPerWeek}
            onChange={e => setHoursPerWeek(e.target.value)} />
        </div>
      </div>
      <p className="settings-note" style={{ marginTop: 6 }}>
        ≈ {perSessionMinutes} min/session, spread across the week (not clumped).
      </p>

      <div className="form-label">Start week <span className="cp-edit-form__hint">(optional — defaults to the current week)</span></div>
      <input className="input input--short" type="number" min={1} placeholder="current week"
        value={startWeekNumber ?? ''} onChange={e => setStartWeekNumber(e.target.value ? Number(e.target.value) : null)} />

      <div className="form-label">Who picks the day?</div>
      <div className="pickup-pattern-tabs">
        {[
          { id: 'student-placed', label: 'Student places it (weekly board)' },
          { id: 'fixed', label: 'I set the days' },
        ].map(tab => (
          <button key={tab.id} type="button"
            className={`pickup-pattern-tab ${placementMode === tab.id ? 'active' : ''}`}
            onClick={() => setPlacementMode(tab.id)}>{tab.label}</button>
        ))}
      </div>

      <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8 }}>
        <input type="checkbox" checked={isFloor} onChange={e => setIsFloor(e.target.checked)} />
        <span style={{ fontSize: 13 }}>Counts toward each day's must-do floor</span>
      </label>
      <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8 }}>
        <input type="checkbox" checked={needsReview} onChange={e => setNeedsReview(e.target.checked)} />
        <span style={{ fontSize: 13 }}>Needs a parent look when complete</span>
      </label>
      <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8 }}>
        <input type="checkbox" checked={needsParent} onChange={e => setNeedsParent(e.target.checked)} />
        <span style={{ fontSize: 13 }}>Needs a parent present to do it <span className="cp-edit-form__hint">(scribing, reading aloud, lab setup)</span></span>
      </label>

      <button className="btn btn--primary" style={{ marginTop: 12 }} disabled={!canSave || saving} onClick={save}>
        {saving ? 'Creating…' : 'Create series'}
      </button>
      {result && <p className="settings-note" style={{ marginTop: 8, color: 'var(--good)' }}>{result}</p>}
    </div>
  );
}
